import { grey, blue, purple } from '@mui/material/colors'
import { useTranslation } from 'react-i18next'
import fi from 'date-fns/locale/fi'
import { Chart, ChartData, ChartDataset, ChartOptions, ScatterDataPoint } from 'chart.js'
import 'chartjs-adapter-date-fns'
import {
  getDifferenceOfDateRangeInHours,
  humanizeDate,
  humanizeDateRange,
  humanizeTimestamp
} from '../utils/date'
import LoadingState from './LoadingState'
import _ from 'lodash'
import { useTheme } from '@mui/material'
import {
  DateRange,
  RuuviTagAccelerationAggregate,
  RuuviTagAccelerationAggregateField,
  RuuviTagAccelerationAggregateQuery,
  RuuviTagAccelerationAggregateStatistic
} from '../types'
import { useDispatch } from 'react-redux'
import { setDateRange, setTimePeriod } from '../redux/slices/query'
import TimePeriod from '../enums/TimePeriod'
import zoom from 'chartjs-plugin-zoom'
import LineChart from './LineChart'

Chart.register(zoom)

export interface RuuviTagAccelerationAggregateChart {
  accelerations: RuuviTagAccelerationAggregate[]
  query: RuuviTagAccelerationAggregateQuery
  fields: RuuviTagAccelerationAggregateField[]
  statistics: RuuviTagAccelerationAggregateStatistic[]

  /**
   * The date range used by charts.
   */
  dateRange?: DateRange

  options?: ChartOptions
  height?: string
  loading?: boolean
}

export default function RuuviTagAccelerationAggregateChart({
  accelerations,
  query,
  fields,
  statistics,
  dateRange,
  options,
  height = '320px',
  loading = false
}: RuuviTagAccelerationAggregateChart) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The difference of date range as hours.
   */
  const differenceOfDateRangeInHours = getDifferenceOfDateRangeInHours(
    dateRange ? dateRange.from : query.from,
    dateRange ? dateRange.to : query.to
  )

  /**
   * The theme.
   */
  const theme = useTheme()

  /**
   * Update the date range.
   */
  function updateDateRange({ chart }: { chart: Chart }) {
    const { min, max } = chart.scales.x

    dispatch(setTimePeriod(TimePeriod.CUSTOM))
    dispatch(
      setDateRange({
        from: new Date(parseInt(min.toString())),
        to: new Date(parseInt(max.toString()))
      })
    )
  }

  /**
   * The chart data.
   */
  const data: ChartData = {
    datasets: makeChartDatasets()
  }

  /**
   * The default chart options.
   */
  const defaultOptions: ChartOptions<'line'> = {
    plugins: {
      zoom: {
        zoom: {
          drag: {
            enabled: true
          },
          mode: 'x',
          onZoomComplete: updateDateRange
        }
      },
      title: {
        display: true,
        position: 'bottom',
        padding: {
          top: 24
        },
        text: `${humanizeDateRange(
          dateRange ? dateRange.from : query.from,
          dateRange ? dateRange.to : query.to
        )}`,
        font: {
          // @ts-expect-error typed incorrectly
          weight: '600',
          size: 14
        },
        color: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 1.0)' : 'rgba(255, 255, 255, 1.0)'
      },
      legend: {
        display: true,
        labels: {
          boxWidth: 24,
          boxHeight: 1,
          padding: 24,
          font: {
            // @ts-expect-error typed incorrectly
            weight: '600',
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems): string => {
            if (tooltipItems.length) {
              return differenceOfDateRangeInHours > 744
                ? // @ts-expect-error typed incorrectly
                  humanizeDate(tooltipItems[0].raw.x)
                : // @ts-expect-error typed incorrectly
                  humanizeTimestamp(tooltipItems[0].raw.x)
            }

            return ''
          }
        }
      },
      decimation: {
        enabled: false,
        algorithm: 'min-max'
      }
    },
    elements: {
      point: {
        pointStyle: 'point',
        radius: 0
      },
      line: {
        borderWidth: 1
      }
    },
    scales: {
      x: {
        type: 'time',
        display: true,
        // @ts-expect-error typed incorrectly
        min: dateRange ? dateRange.from : query.from,
        // @ts-expect-error typed incorrectly
        max: dateRange ? dateRange.to : query.to,
        time: {
          unit:
            differenceOfDateRangeInHours <= 3
              ? 'minute'
              : differenceOfDateRangeInHours <= 24
                ? 'hour'
                : 'day',
          displayFormats: {
            minute: 'HH:mm',
            hour: 'HH:mm',
            day: 'd.M'
          }
        },
        adapters: {
          date: {
            locale: fi
          }
        },
        ticks: {
          autoSkip: true,
          maxRotation: 0,
          source: 'auto'
        },
        grid: {
          display: true,
          color: 'rgba(255, 255, 255, 0.0375)',
          // @ts-expect-error typed incorrectly
          fontColor: '#fff',
          drawBorder: false
        },
        color: '#fff'
      },
      y: {
        display: true,
        grid: {
          display: true,
          color: 'rgba(255, 255, 255, 0.0375)',
          // @ts-expect-error typed incorrectly
          fontColor: '#fff',
          drawBorder: false
        }
      }
    }
  }

  /**
   * The merged chart options.
   */
  options = _.merge(defaultOptions, options)

  function makeChartDatasets(): ChartDataset[] {
    const datasets: ChartDataset[] = []

    for (const field of fields) {
      for (const statistic of statistics) {
        const label = makeDatasetLabel(field, statistic)
        const color = makeDatasetColor(field, statistic)
        const data = makeDatasetData(field, statistic)

        datasets.push({
          label,
          backgroundColor: color,
          borderColor: color,
          tension: 0.4,
          spanGaps: false,
          data: data as unknown as ScatterDataPoint[]
        })
      }
    }

    return datasets
  }

  /**
   * Make the key.
   */
  function makeKey(
    field: RuuviTagAccelerationAggregateField,
    statistic: RuuviTagAccelerationAggregateStatistic
  ): keyof RuuviTagAccelerationAggregate {
    return _.camelCase(`${field}_${statistic}`) as keyof RuuviTagAccelerationAggregate
  }

  /**
   * Make the label for the dataset.
   */
  function makeDatasetLabel(
    field: RuuviTagAccelerationAggregateField,
    statistic: RuuviTagAccelerationAggregateStatistic
  ): string {
    return `${field.toUpperCase()} (${t(statistic)})`
  }

  /**
   * Make the color for the dataset.
   */
  function makeDatasetColor(
    field: RuuviTagAccelerationAggregateField,
    statistic: RuuviTagAccelerationAggregateStatistic
  ) {
    let weight: 'A100' | 'A700' | '500'
    switch (statistic) {
      case 'min':
        weight = 'A100'
        break
      case 'max':
        weight = 'A700'
        break
      case 'avg':
      default:
        weight = '500'
        break
    }

    // @see https://mui.com/material-ui/customization/color/#color-palette
    switch (field) {
      case 'x':
        return grey[weight]
      case 'y':
        return purple[weight]
      case 'z':
      default:
        return blue[weight]
    }
  }

  /**
   * Make the data for the dataset.
   */
  function makeDatasetData(
    field: RuuviTagAccelerationAggregateField,
    statistic: RuuviTagAccelerationAggregateStatistic
  ) {
    const key = makeKey(field, statistic)

    return accelerations.map((acceleration) => {
      return {
        x: acceleration.timeBucket,
        y: acceleration[key]
      }
    })
  }

  return (
    <>
      {loading ? (
        <LoadingState />
      ) : (
        <>
          <LineChart data={data} options={options} wrapperHeight={height} />
        </>
      )}
    </>
  )
}
